import { MultiSelect } from "@/components/ui/multi-select";
import { updateSearchParams } from "@/lib/utils";
import { useRouter } from "@bprogress/next";
import { useSearchParams } from "next/navigation";
import { useEffect, useMemo, useState } from "react";

export function MultiSelectFilter({
  filter,
  onChange,
  disabled = false,
}: {
  filter: Filter;
  onChange?: (selected: FilterOption[]) => void;
  disabled?: boolean;
  theme?: "filter" | "sort";
  faintIcons?: boolean;
}) {
  const router = useRouter();
  const { name, placeholder, options } = filter;
  const value = (filter.value as FilterOptionValue[]) ?? null;

  function sortOptions(
    selectedValues: FilterOptionValue[],
    options: FilterOption[],
  ) {
    return [...options].sort((a, b) => {
      const aSelected = selectedValues.includes(a.value);
      const bSelected = selectedValues.includes(b.value);

      if (aSelected && !bSelected) {
        return -1;
      } else if (!aSelected && bSelected) {
        return 1;
      }
      return 0;
    });
  }

  const sortedOptions = useMemo(() => {
    const selectedValues = (filter.value as FilterOptionValue[]) || [];
    return sortOptions(selectedValues, options ?? []);
  }, [options, value]);

  function parseFilterOptions(options: FilterOption[]) {
    return options.map((option) => ({
      name: String(option.name),
      value: String(option.value),
    }));
  }

  const [currentOptions, setCurrentOptions] = useState<FilterOption[]>(
    sortedOptions ?? [],
  );

  // used only for clearing filter when search params change
  const searchParams = useSearchParams();

  useEffect(() => {
    if (!searchParams.has(name)) {
      setCurrentOptions(sortedOptions ?? []);
    }
  }, [searchParams, name, filter.value]);
  //

  return (
    <div>
      <MultiSelect
        options={parseFilterOptions(currentOptions)}
        onValueChange={(values: string[]) => {
          const selectedOptions = sortedOptions.filter((option) =>
            values.includes(String(option.value)),
          );
          if (onChange) {
            onChange(selectedOptions);
          }
          setCurrentOptions(sortOptions(values, currentOptions));
          updateSearchParams(
            name,
            selectedOptions.map((option) => String(option.value)),
            router,
          );
        }}
        defaultValue={value?.map((v) => String(v)) || []}
        placeholder={placeholder}
        variant="inverted"
        className={"text-foreground rounded-lg border-[#B2BAC5] shadow-none"}
        disabled={disabled}
      />
    </div>
  );
}
